import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, hasEverAuthenticated } from "@/contexts/AuthContext";

/** Gate component — redirects unauthenticated visitors:
 *   - Returning user (has-account marker) -> /login
 *   - Brand-new visitor (no marker)        -> / (combined signup landing)
 *  This makes "Welcome back." copy only show to people who actually have an account.
 *  While auth state is being checked it shows a spinner. */
export default function RequireAuth({ children }) {
  const { isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const next = encodeURIComponent(location.pathname + location.search);
      if (hasEverAuthenticated()) {
        navigate(`/login?next=${next}`, { replace: true });
      } else {
        // Brand-new visitor: send them to the combined signup landing page,
        // not to /login. ?stay=1 prevents the landing's own auto-redirect
        // from bouncing them right back here.
        navigate(`/?stay=1`, { replace: true });
      }
    }
  }, [isLoading, isAuthenticated, navigate, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F9]">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (!isAuthenticated) return null;
  return children;
}
